import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import { api } from "../lib/api";

export default function Events() {
  const { currentContainer } = useAuth();
  const [filters, setFilters] = useState({
    event_name: "",
    date_from: "",
    date_to: "",
    page: 1,
  });

  const { data: events, isLoading } = useQuery({
    queryKey: ["events", currentContainer?.id, filters],
    queryFn: () => api.getEvents(filters),
    enabled: !!currentContainer,
  });

  const { data: eventTypes } = useQuery({
    queryKey: ["event-types", currentContainer?.id],
    queryFn: api.getEventTypes,
    enabled: !!currentContainer,
  });

  if (!currentContainer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Select an app to view events</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Events</h1>
        <p className="text-gray-500">Browse and filter tracked events</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={filters.event_name}
              onChange={(e) => setFilters({ ...filters, event_name: e.target.value, page: 1 })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">All Events</option>
              {eventTypes?.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value, page: 1 })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value, page: 1 })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ event_name: "", date_from: "", date_to: "", page: 1 })}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading events...</div>
        ) : events?.data?.length > 0 ? (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Context
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Properties
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.data.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{event.event_name}</div>
                      <div className="text-xs text-gray-400">{event.app_name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="space-y-1">
                        {event.user_id && (
                          <div><span className="text-gray-400">User:</span> {event.user_id}</div>
                        )}
                        {event.container_id && (
                          <div>
                            <span className="text-gray-400 capitalize">{event.container_name || 'Account'}:</span> {event.container_id}
                          </div>
                        )}
                        {event.session_jti && (
                          <div><span className="text-gray-400">Session:</span> <span className="font-mono text-xs">{event.session_jti.slice(0, 8)}...</span></div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {Object.keys(event.properties || {}).length > 0 ? (
                        <pre className="text-xs text-gray-500 max-w-md overflow-hidden">
                          {JSON.stringify(event.properties, null, 2)}
                        </pre>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(event.occurred_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {events.meta && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {events.meta.from} to {events.meta.to} of {events.meta.total} events
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={filters.page <= 1}
                    className="px-3 py-1 text-sm rounded border disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={filters.page >= events.meta.last_page}
                    className="px-3 py-1 text-sm rounded border disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">No events found</div>
        )}
      </div>
    </div>
  );
}
